import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress } from "@mui/material";
import { deleteIcon } from "../../../assets/images";
import {
  TeacherNotificationItem,
  notificationService,
} from "../../../services/notifications/notification.service";
import StudentExercisePreviewModal from "./StudentExercisePreviewModal";
import ExerciseReviewContent from "./ExerciseReviewContent";
import "./NotificationDetailsModal.scss";

interface NotificationDetailsModalProps {
  notification: TeacherNotificationItem | null;
  onClose: () => void;
  onDelete: (id: number) => void;
}

export const NotificationDetailsModal: React.FC<NotificationDetailsModalProps> = ({
  notification,
  onClose,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [exerciseDrawerId, setExerciseDrawerId] = useState<number | null>(null);
  const [detailData, setDetailData] = useState<TeacherNotificationItem | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);

  // Keyboard escape listener
  useEffect(() => {
    if (!notification) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [notification, onClose]);

  // On-demand O(1) full detail fetching
  useEffect(() => {
    if (!notification) {
      setDetailData(null);
      return;
    }

    // If it's not an exercise review, or if full review details are already present
    if (
      notification.notification_type !== "exercise_review" ||
      notification.metadata?.analysis_summary ||
      (notification.metadata?.strengths && notification.metadata.strengths.length > 0)
    ) {
      setDetailData(notification);
      return;
    }

    setDetailData(notification);
    let isMounted = true;
    setIsLoadingDetail(true);

    notificationService
      .getNotificationById(notification.id)
      .then((fullNotif) => {
        if (isMounted && fullNotif) {
          setDetailData(fullNotif);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch notification details:", err);
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingDetail(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [notification?.id]);

  if (!notification) return null;

  const activeNotification = detailData || notification;

  const initials =
    notification.sender_initials ||
    (notification.sender_name
      ? notification.sender_name
          .trim()
          .split(" ")
          .map((part) => part[0])
          .slice(0, 2)
          .join("")
      : t("teacherInitialsFallback"));

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(notification.id);
  };

  return (
    <div
      className="notification-details-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="notification-details-teacher-name"
    >
      {/* Dimmed Backdrop */}
      <div
        className="notification-details-modal__backdrop"
        onClick={onClose}
        onTouchMove={(e) => e.preventDefault()}
        aria-hidden="true"
      />

      {/* Static Bottom Sheet Paper */}
      <div className="notification-details-modal__paper">
        {/* Visual Top Bar Indicator */}
        <div className="notification-details-modal__handle-bar" />

        {/* Top Header Row (RTL layout) */}
        <div className="notification-details-modal__header">
          {/* RTL Start (Right): Teacher Avatar & Identity */}
          <div className="notification-details-modal__teacher-info">
            {notification.sender_avatar_url ? (
              <img
                src={notification.sender_avatar_url}
                alt={notification.sender_name || t("teacher")}
                className="notification-details-modal__avatar"
              />
            ) : (
              <div className="notification-details-modal__avatar v2-user-message__avatar-placeholder">
                <span>{initials}</span>
              </div>
            )}

            <div className="notification-details-modal__titles">
              <span
                id="notification-details-teacher-name"
                className="notification-details-modal__teacher-name"
              >
                {notification.sender_name || t("teacher")}
              </span>
              <span className="notification-details-modal__subtitle">
                {t("mathTeacher")}
              </span>
            </div>
          </div>

          {/* RTL End (Left): Action buttons */}
          <div className="notification-details-modal__actions">
            <button
              type="button"
              className="notification-details-modal__icon-btn notification-details-modal__icon-btn--delete"
              onClick={handleDelete}
              title={t("delete")}
              aria-label={t("delete")}
            >
              <img
                src={deleteIcon}
                alt={t("delete")}
                className="notification-details-modal__delete-icon"
              />
            </button>
            <button
              type="button"
              className="notification-details-modal__icon-btn notification-details-modal__icon-btn--close"
              onClick={onClose}
              title={t("close")}
              aria-label={t("close")}
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </div>

        {/* Message Bubble Body */}
        <div className="notification-details-modal__body">
          {isLoadingDetail ? (
            <div className="notification-details-modal__loading-state">
              <CircularProgress size={32} className="details-spinner" />
            </div>
          ) : activeNotification.notification_type === "exercise_review" ? (
            <ExerciseReviewContent
              notification={activeNotification}
              onOpenExercise={(exerciseId) => setExerciseDrawerId(exerciseId)}
            />
          ) : (
            <div className="notification-details-modal__message-bubble">
              <p className="notification-details-modal__message-text">
                {activeNotification.body}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Student Exercise Preview Drawer (matching exercise-practice orange-calculator-tab drawer) */}
      <StudentExercisePreviewModal
        open={Boolean(exerciseDrawerId)}
        onClose={() => setExerciseDrawerId(null)}
        exerciseId={exerciseDrawerId}
      />
    </div>
  );
};

export default NotificationDetailsModal;
